import { BenefitDTO } from 'probonio-shared-ui/api';
import { EXTENSIONS } from 'probonio-shared-ui/registry';
import { ExtensionInterface } from 'probonio-shared-ui/registry/extension-definition';
import React from 'react';

/**
 * Interface for extensions that can provide additional benefit list items
 * beyond the standard benefit processing. This is useful for cases like
 * standalone coupons that need special logic to determine when they should appear.
 */
export abstract class AdditionalBenefitListItemsExtensionInterface extends ExtensionInterface {
  /**
   * Defines if these additional items should be positioned at the top.
   * Default is false (bottom position).
   * Set to true for items that should appear at the top (like gifts).
   */
  showAtTop: boolean = false;

  /**
   * Gets additional benefit list items that should be rendered
   * @param activeBenefits - All active benefits
   * @returns Array of additional React nodes to render
   */
  abstract getAdditionalItems(activeBenefits: BenefitDTO[]): React.ReactNode[];
}

/**
 * Retrieves additional benefit list items for a specific position.
 *
 * @param activeBenefits - All active benefits
 * @param showAtTop - Whether to get items that should be shown at the top (true) or bottom (false)
 * @returns Array of additional React nodes to render
 */
export function getAdditionalBenefitListItems(activeBenefits: BenefitDTO[], showAtTop: boolean): React.ReactNode[] {
  const extensions = EXTENSIONS.getProviders(AdditionalBenefitListItemsExtensionInterface);

  const additionalItems: React.ReactNode[] = [];

  extensions
    .filter(extension => extension.showAtTop === showAtTop)
    .forEach(extension => {
      const items = extension.getAdditionalItems(activeBenefits);
      additionalItems.push(...items);
    });

  return additionalItems;
}
